import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { DELETE_fAQ } from "src/graphql/mutations";
import { GET_FAQS } from "src/graphql/query";
import { debounce } from "src/utils/debounce";
import CustomTable from "src/components/CustomTable";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomButton from "src/components/CustomButton";
import FaqModal from "./FaqModal";
import DeleteModel from "src/components/Common/DeleteModel";
import { toast } from "react-toastify";
import { ClearIcon } from "@mui/x-date-pickers";
import Select from "react-select";
import {
  DELETE_ALL_FAQS,
  DELETE_MULTIPLE_FAQS,
} from "src/graphql/DeleteMutation";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import ActionMenu from "../Business/ActionMenu";

export interface Faq {
  _id: string;
  question: string;
  answer: string;
}

const enum type {
  All = "All",
  HOMEPAGE = "HOMEPAGE",
}

const Faq: React.FC = () => {
  const [list, setList] = useState<Faq[]>([]);
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
  const [total, setTotal] = useState(0);
  const [deleteFaq] = useMutation(DELETE_fAQ);
  const [openDeleteModel, setOpenDeleteModel] = useState<boolean>(false);
  const [deleteFaqId, setDeleteFaqId] = useState<string | null>(null);
  const [mode, setMode] = useState<type>(type.All);
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] =
    useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const [DeleteMultipleFaqs] = useMutation(DELETE_MULTIPLE_FAQS);
  const [DeleteAllFaqs] = useMutation(DELETE_ALL_FAQS);
  const [selectedRows, setSelectedRows] = useState([]);
  const isSuperAdmin = getRole() === "SuperAdmin";
  useState<string>("");
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const options = [
    { value: type.All, label: "All" },
    { value: type.HOMEPAGE, label: "Homepage" },
  ];

  const COLUMNS = [
    ...(isSuperAdmin
      ? [
        {
          headerName: (
            <Box display={"flex"} alignItems={"center"}>
              <Checkbox
                checked={
                  selectedRows.length === list.length && list.length > 0
                }
                indeterminate={
                  selectedRows.length > 0 && selectedRows.length < list.length
                }
                onChange={(event) => handleSelectAll(event.target.checked)}
              />
            </Box>
          ),
          field: "select",
          flex: 0.5,
          sortable: false,
          filterable: false,
          disableClickEventBubbling: true,
          renderCell: ({ row }: any) => (
            <Checkbox
              checked={selectedRows.includes(row?._id as never)}
              onChange={() => handleRowSelect(row._id)}
            />
          ),
        },
      ]
      : []),
    {
      headerName: "Question",
      field: "question",
      width: 600,
      renderCell: ({ value, row }: { value: string; row: any }) => {
        const expanded = expandedRows[row._id] || false;
        const maxLength = 80; // Adjust as needed

        return (
          <Box
            sx={{
              whiteSpace: expanded ? "pre-wrap" : "nowrap",
              overflow: "hidden",
              textOverflow: expanded ? "clip" : "ellipsis",
              width: "100%",
            }}
          >
            {expanded ? value : value.length > maxLength ? `${value.substring(0, maxLength)}...` : value}
            {value.length > maxLength && (
              <span
                onClick={(e) => {
                  e.stopPropagation(); // Prevents row selection
                  toggleExpand(row._id);
                }}
                style={{ color: "#00C5B9", cursor: "pointer", marginLeft: "10px" }}
              >
                {expanded ? "See Less" : "See More"}
              </span>
            )}
          </Box>
        );
      },
    },
    { headerName: "Answer", field: "answer", width: 780,
      renderCell: ({ value, row }: { value: string; row: any }) => {
        const expanded = expandedRows[row._id] || false;
        const maxLength = 90; // Adjust as needed

        return (
          <Box
            sx={{
              whiteSpace: expanded ? "pre-wrap" : "nowrap",
              overflow: "hidden",
              textOverflow: expanded ? "clip" : "ellipsis",
              width: "100%",
            }}
          >
            {expanded ? value : value.length > maxLength ? `${value.substring(0, maxLength)}...` : value}
            {value.length > maxLength && (
              <span
                onClick={(e) => {
                  e.stopPropagation(); // Prevents row selection
                  toggleExpand(row._id);
                }}
                style={{ color: "#00C5B9", cursor: "pointer", marginLeft: "10px" }}
              >
                {expanded ? "See Less" : "See More"}
              </span>
            )}
          </Box>
        );
      },
     },
    {
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }: { row: { _id: string } }) => (
        <Box>
          {hasPermissionPage("Faq", "update") && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleEdit(row)}
                aria-label="edit"
                style={{ marginRight: "8px", color: "#00C5B9" }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionPage("Faq", "delete") && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  setDeleteFaqId(row._id);
                  setOpenDeleteModel(true);
                }}
                aria-label="delete"
                style={{ color: "#00C5B9" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const { loading, error, data, refetch } = useQuery(GET_FAQS, {
    variables: {
      page: pagination.page + 1,
      limit: pagination.pageSize,
      filter: mode,
    },
  });

  const handleRowSelect = (id: any) => {
    setSelectedRows((prevSelectedRows: any) =>
      prevSelectedRows.includes(id)
        ? prevSelectedRows.filter((rowId: any) => rowId !== id)
        : [...prevSelectedRows, id]
    );
  };
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allRowIds = list.map((row: any) => row._id);
      setSelectedRows(allRowIds as any);
    } else {
      setSelectedRows([]);
    }
  };

  const handleMultipleDelete = async () => {
    try {
      const res = await DeleteMultipleFaqs({
        variables: {
          _id: selectedRows,
        },
      });
      if (res?.data?.deleteMultipleFaqs?.statusCode === 200) {
        toast.success(res?.data?.deleteMultipleFaqs?.message);
        refetch();
      } else {
        throw new Error(res?.data?.deleteMultipleFaqs?.message);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message);
    } finally {
      setOpenDeleteMultipleModel(false);
    }
  };

  const handleAllDelete = async () => {
    try {
      const res = await DeleteAllFaqs();
      if (res?.data?.deleteAllFaqs?.statusCode === 200) {
        toast.success(res?.data?.deleteAllFaqs?.message);
        refetch();
      } else {
        throw new Error(res?.data?.deleteAllFaqs?.message);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message);
    } finally {
      setOpenDeleteAllModel(false);
    }
  };

  const debouncedRefetch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
      refetch({
        search: term,
        page: pagination.page + 1,
        limit: pagination.pageSize,
      });
    }, 1000),
    [refetch, pagination.page, pagination.pageSize, mode]
  );

  useEffect(() => {
    if (data?.getFaqs?.data) {
      setList(data.getFaqs.data);
    }
    setTotal(data?.getFaqs?.count);
  }, [data]);

  const handleEdit = (rowData: any) => {
    setSelectedFaq(rowData);
    setOpenModal(true);
  };

  const handleDelete = async () => {
    if (!deleteFaqId) return;
    try {
      const res = await deleteFaq({
        variables: { id: deleteFaqId },
      });
      if (res?.errors) {
        throw new Error(res?.errors as any);
      } else {
        toast.success(res?.data?.deleteFaq?.message);
        refetch();
        setOpenDeleteModel(false);
      }
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFaq(null);
  };

  const handleCloseDeleteModel = () => {
    setOpenDeleteModel(false);
    setDeleteFaqId(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedRefetch(value);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage === pagination?.page) {
      return;
    }
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  const handleModeChange = (selectedOption: any) => {
    setMode(selectedOption.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({
      filter: selectedOption.value,
      page: 1,
      limit: pagination.pageSize,
    });
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" style={{ color: "#00C5B9" }}>
          Faq
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <Box width={200}>
            <Select
              value={options.find((option) => option.value === mode)}
              onChange={handleModeChange}
              options={options}
            />
          </Box>
          <Box width={200}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {isSuperAdmin &&
            <ActionMenu
              isSuperAdmin={isSuperAdmin}
              setOpenDeleteMultipleModel={setOpenDeleteMultipleModel}
              setOpenDeleteAllModel={setOpenDeleteAllModel}
            />
          }
          {hasPermissionPage("Faq", "add") && (
            <Box>
              <CustomButton
                onClick={() => setOpenModal(true)}
                variant="contained"
              >
                Create
              </CustomButton>
            </Box>
          )}
        </Box>
      </Box>
      <Divider />
      <Box mt={2}>
        <CustomTable
          columns={COLUMNS}
          data={list}
          paginationModel={{
            page: pagination.page,
            pageSize: pagination.pageSize,
          }}
          totalCount={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          getRowHeight={({ id }) => (expandedRows[id] ? "auto" : null)}
        />
      </Box>
      <FaqModal
        open={openModal}
        handleClose={handleCloseModal}
        faq={selectedFaq}
        refetchFaq={refetch}
      />
      {openDeleteModel && (
        <DeleteModel
          open={openDeleteModel}
          onClose={handleCloseDeleteModel}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this FAQ?"
        />
      )}
      {openDeleteMultipleModel && (
        <DeleteModel
          open={openDeleteMultipleModel}
          onClose={() => {
            setOpenDeleteMultipleModel(false);
          }}
          onConfirm={handleMultipleDelete}
          message={`Are you sure you want to delete ${selectedRows?.length ?? 0
            } faqs?`}
        />
      )}
      {openDeleteAllModel && (
        <DeleteModel
          open={openDeleteAllModel}
          onClose={() => {
            setOpenDeleteAllModel(false);
          }}
          onConfirm={handleAllDelete}
          message="Are you sure you want to delete all faqs?"
        />
      )}
    </Box>
  );
};

export default Faq;
